import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import PageLoader from "./components/PageLoader";
import api from "./api";

interface Milestone {
  threshold: number;
  label: string;
  rewardDisplay: string;
}

interface VolumeConfig {
  _id?: string;
  title: string;
  description: string;
  targetVolumeUsd: number;
  enabled: boolean;
  milestones: Milestone[];
}

const DEFAULT_CONFIG: VolumeConfig = {
  title: "Your Monthly Trade Volume Target",
  description: "Complete the volume target to unlock stablecoin rewards",
  targetVolumeUsd: 10000,
  enabled: true,
  milestones: [
    { threshold: 1000,  label: "$1K",   rewardDisplay: "+50 XP" },
    { threshold: 2500,  label: "$2.5K", rewardDisplay: "+100 XP" },
    { threshold: 5000,  label: "$5K",   rewardDisplay: "$5 USDC" },
    { threshold: 7500,  label: "$7.5K", rewardDisplay: "$15 USDC" },
    { threshold: 10000, label: "$10K",  rewardDisplay: "$50 USDC" },
  ],
};

export default function VolumeChallenge() {
  const [config, setConfig] = useState<VolumeConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null!);

  useEffect(() => {
    api
      .get("/api/arena/volume-config")
      .then((res) => setConfig(res.data.data ?? DEFAULT_CONFIG))
      .catch(() => toast.error("Failed to load config"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/api/arena/volume-config", config);
      setConfig(res.data.data);
      toast.success("Volume challenge config saved!");
    } catch {
      toast.error("Failed to save config");
    } finally {
      setSaving(false);
    }
  };

  const updateMilestone = (idx: number, field: keyof Milestone, value: string | number) => {
    setConfig((prev) => {
      const milestones = [...prev.milestones];
      milestones[idx] = { ...milestones[idx], [field]: value };
      return { ...prev, milestones };
    });
  };

  const addMilestone = () => {
    setConfig((prev) => ({
      ...prev,
      milestones: [...prev.milestones, { threshold: 0, label: "", rewardDisplay: "" }],
    }));
  };

  const removeMilestone = (idx: number) => {
    setConfig((prev) => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== idx),
    }));
    setDeleteIdx(null);
  };

  const sortMilestones = () => {
    setConfig((prev) => ({
      ...prev,
      milestones: [...prev.milestones].sort((a, b) => a.threshold - b.threshold),
    }));
    toast.success("Milestones sorted by threshold");
  };

  if (loading) return <PageLoader />;

  return (
    <Container maxW="container.lg" py={8}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="white">
            Volume Challenge
          </Heading>
          <Text color="gray.400" mt={1} fontSize="sm">
            Configure the monthly trade volume target shown on the Arena page
          </Text>
        </Box>
        <HStack>
          <Badge
            colorScheme={config.enabled ? "green" : "red"}
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
          >
            {config.enabled ? "Active" : "Disabled"}
          </Badge>
          <Button
            colorScheme="brand"
            onClick={save}
            isLoading={saving}
            loadingText="Saving…"
            size="sm"
          >
            Save Changes
          </Button>
        </HStack>
      </Flex>

      <VStack spacing={6} align="stretch">
        {/* General settings card */}
        <Box bg="gray.800" borderRadius="xl" border="1px solid" borderColor="gray.700" p={6}>
          <Heading size="sm" color="gray.300" mb={4} textTransform="uppercase" letterSpacing="wider">
            General Settings
          </Heading>

          <VStack spacing={4} align="stretch">
            {/* Enabled toggle */}
            <FormControl display="flex" alignItems="center" gap={4}>
              <Switch
                id="enabled"
                isChecked={config.enabled}
                onChange={(e) => setConfig((p) => ({ ...p, enabled: e.target.checked }))}
                colorScheme="green"
                size="lg"
              />
              <FormLabel htmlFor="enabled" mb={0} color="white" cursor="pointer">
                Show Volume Challenge on Arena page
              </FormLabel>
            </FormControl>

            <Divider borderColor="gray.700" />

            {/* Title */}
            <FormControl>
              <FormLabel color="gray.400" fontSize="sm">
                Title
              </FormLabel>
              <Input
                value={config.title}
                onChange={(e) => setConfig((p) => ({ ...p, title: e.target.value }))}
                bg="gray.900"
                borderColor="gray.600"
                color="white"
                _hover={{ borderColor: "gray.500" }}
                _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
                placeholder="e.g. Your Monthly Trade Volume Target"
              />
            </FormControl>

            {/* Description */}
            <FormControl>
              <FormLabel color="gray.400" fontSize="sm">
                Description
              </FormLabel>
              <Textarea
                value={config.description}
                onChange={(e) => setConfig((p) => ({ ...p, description: e.target.value }))}
                bg="gray.900"
                borderColor="gray.600"
                color="white"
                _hover={{ borderColor: "gray.500" }}
                _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
                placeholder="e.g. Complete $10,000 volume target to unlock stablecoin reward"
                rows={2}
              />
            </FormControl>

            {/* Target volume */}
            <FormControl>
              <FormLabel color="gray.400" fontSize="sm">
                Target Volume (USD)
              </FormLabel>
              <NumberInput
                value={config.targetVolumeUsd}
                onChange={(_, v) => setConfig((p) => ({ ...p, targetVolumeUsd: isNaN(v) ? 0 : v }))}
                min={0}
                step={1000}
              >
                <NumberInputField
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  _hover={{ borderColor: "gray.500" }}
                  _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper borderColor="gray.600" color="gray.400" />
                  <NumberDecrementStepper borderColor="gray.600" color="gray.400" />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </VStack>
        </Box>

        {/* Milestones card */}
        <Box bg="gray.800" borderRadius="xl" border="1px solid" borderColor="gray.700" p={6}>
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="sm" color="gray.300" textTransform="uppercase" letterSpacing="wider">
              Milestones ({config.milestones.length})
            </Heading>
            <HStack>
              <Button
                size="xs"
                variant="ghost"
                color="gray.400"
                onClick={sortMilestones}
                title="Sort milestones by threshold"
              >
                Sort ↑
              </Button>
              <Button
                size="sm"
                colorScheme="brand"
                leftIcon={<AddIcon boxSize={3} />}
                onClick={addMilestone}
              >
                Add Milestone
              </Button>
            </HStack>
          </Flex>

          {config.milestones.length === 0 ? (
            <Alert status="info" bg="gray.700" borderRadius="lg">
              <AlertIcon />
              <Text color="gray.300" fontSize="sm">
                No milestones configured. Click "Add Milestone" to create one.
              </Text>
            </Alert>
          ) : (
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th color="gray.400" borderColor="gray.700" width="180px">
                    Threshold (USD)
                  </Th>
                  <Th color="gray.400" borderColor="gray.700" width="130px">
                    Label
                  </Th>
                  <Th color="gray.400" borderColor="gray.700">
                    Reward Display
                  </Th>
                  <Th color="gray.400" borderColor="gray.700" width="60px" />
                </Tr>
              </Thead>
              <Tbody>
                {config.milestones.map((m, idx) => (
                  <Tr key={idx} _hover={{ bg: "gray.750" }}>
                    <Td borderColor="gray.700" py={2}>
                      <NumberInput
                        value={m.threshold}
                        onChange={(_, v) => updateMilestone(idx, "threshold", isNaN(v) ? 0 : v)}
                        min={0}
                        size="sm"
                      >
                        <NumberInputField
                          bg="gray.900"
                          borderColor="gray.600"
                          color="white"
                          fontSize="sm"
                          _focus={{ borderColor: "brand.400" }}
                        />
                      </NumberInput>
                    </Td>
                    <Td borderColor="gray.700" py={2}>
                      <Input
                        value={m.label}
                        onChange={(e) => updateMilestone(idx, "label", e.target.value)}
                        placeholder="$1K"
                        size="sm"
                        bg="gray.900"
                        borderColor="gray.600"
                        color="white"
                        _focus={{ borderColor: "brand.400" }}
                      />
                    </Td>
                    <Td borderColor="gray.700" py={2}>
                      <Input
                        value={m.rewardDisplay}
                        onChange={(e) => updateMilestone(idx, "rewardDisplay", e.target.value)}
                        placeholder="+50 XP  or  $5 USDC"
                        size="sm"
                        bg="gray.900"
                        borderColor="gray.600"
                        color="white"
                        _focus={{ borderColor: "brand.400" }}
                      />
                    </Td>
                    <Td borderColor="gray.700" py={2} textAlign="center">
                      <IconButton
                        aria-label="Delete milestone"
                        icon={<DeleteIcon />}
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => setDeleteIdx(idx)}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}

          <Text color="gray.500" fontSize="xs" mt={3}>
            Reward Display accepts any text — e.g. "+50 XP", "$5 USDC", "🎟 Raffle Ticket"
          </Text>
        </Box>

        {/* Preview */}
        <Box bg="gray.800" borderRadius="xl" border="1px solid" borderColor="gray.700" p={6}>
          <Heading size="sm" color="gray.300" mb={4} textTransform="uppercase" letterSpacing="wider">
            Preview
          </Heading>
          <Box
            bg="gray.900"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.600"
            p={4}
            fontFamily="mono"
            fontSize="sm"
          >
            <Text color="blue.300" fontWeight="bold">
              {config.title || "—"}
            </Text>
            <Text color="gray.400" mt={1}>
              {config.description || "—"}
            </Text>
            <Text color="gray.500" mt={2}>
              Target: <Text as="span" color="yellow.300">${config.targetVolumeUsd.toLocaleString()}</Text>
            </Text>
            <Flex mt={3} gap={4} flexWrap="wrap">
              {config.milestones.map((m, i) => (
                <Box key={i} textAlign="center">
                  <Text color="gray.300" fontSize="xs">
                    {m.label || `$${m.threshold}`}
                  </Text>
                  <Text
                    color={m.rewardDisplay.includes("USDC") ? "yellow.400" : "green.400"}
                    fontWeight="bold"
                    fontSize="xs"
                  >
                    {m.rewardDisplay || "—"}
                  </Text>
                </Box>
              ))}
            </Flex>
          </Box>
        </Box>

        {/* Bottom save button */}
        <Flex justify="flex-end">
          <Button
            colorScheme="brand"
            onClick={save}
            isLoading={saving}
            loadingText="Saving…"
          >
            Save Changes
          </Button>
        </Flex>
      </VStack>

      {/* Delete confirmation */}
      <AlertDialog
        isOpen={deleteIdx !== null}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteIdx(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" borderColor="gray.700">
            <AlertDialogHeader color="white">Delete Milestone</AlertDialogHeader>
            <AlertDialogBody color="gray.300">
              Remove milestone{" "}
              <Text as="span" fontWeight="bold" color="white">
                {deleteIdx !== null ? config.milestones[deleteIdx]?.label || `#${deleteIdx + 1}` : ""}
              </Text>
              ? This cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" color="gray.400" onClick={() => setDeleteIdx(null)}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={() => deleteIdx !== null && removeMilestone(deleteIdx)}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
}
